// The Phoenix badge: the drawing (phoenix-art.ts, traced from the design's phoenix-source.webp) set
// alight. Design: design/hot-type/boards/AchievementPhoenix.html, whose boards/phoenix.js this ports.
//
// The wings sway from the shoulders, each feather end rustles on its own beat, the tail sways and its
// flame tips flicker. It burns: the strokes run orange to crimson round the white-hot heart of its
// head and chest, bands of flame rise through it and its edges lick. Every 8 seconds it's reborn, as
// the game's phoenix rises from the ashes: it flares (a flash, a ring of heat, sparks), a burn line
// sweeps it to ash, and the fire climbs back up from the tail.
//
// lit is the share of categories Ablaze: 0 is the phoenix in its ashes, a share between is it
// rekindled that far up from the tail (still, under a glowing burn line), 1 is risen. simple (32px
// and below) fills its box, keeps the fire and a few big embers and drops the motion; on the night
// theme its fire burns brighter, so the wing tips don't sink into a dark row. Reduced motion holds
// it still.
import { emberLayer, type Ember } from './badges.ts'
import { PHOENIX_ART as ART } from './phoenix-art.ts'

const f = (n: number) => Math.round(n * 100) / 100
const DUR = 8
type Point = readonly [number, number]

// The cycle, as SMIL keyTimes: burn, flare, ash, reborn.
const T = { flare: 0.6, peak: 0.645, ashIn: 0.655, ashFull: 0.74, ashHold: 0.82, reborn: 0.965 }
const spline = (n: number) => Array(n).fill('.4 0 .2 1').join(';')
const cycle = (attr: string, values: readonly number[], times: readonly number[]) =>
  `<animate attributeName="${attr}" values="${values.join(';')}" keyTimes="${times.join(';')}" dur="${DUR}s" repeatCount="indefinite" calcMode="spline" keySplines="${spline(times.length - 1)}"/>`

// A gentle back-and-forth rotation about a point, added to whatever else turns the part.
const sway = (deg: number, [x, y]: Point, dur: number, begin = 0) =>
  `<animateTransform attributeName="transform" type="rotate" values="0 ${x} ${y};${deg} ${x} ${y};0 ${x} ${y}" dur="${dur}s" begin="-${f(begin)}s" repeatCount="indefinite" calcMode="spline" keySplines=".45 0 .55 1;.45 0 .55 1" additive="sum"/>`
// The rebirth's wing beat: up on the flare, down in the ash, up again with the fire.
const beat = (up: number, [x, y]: Point) => {
  const r = (deg: number) => `${f(deg * up)} ${x} ${y}`
  return `<animateTransform attributeName="transform" type="rotate" values="${r(0)};${r(0)};${r(8)};${r(3)};${r(-5)};${r(-5)};${r(2)};${r(0)}" keyTimes="0;${T.flare};${T.peak};.7;${T.ashFull + 0.02};${T.ashHold};.95;1" dur="${DUR}s" repeatCount="indefinite" calcMode="spline" keySplines="${spline(7)}" additive="sum"/>`
}

function wing(side: 'left' | 'right', still: boolean, cycling: boolean) {
  const w = ART[side]
  const out = side === 'left' ? -1 : 1
  const move = still ? '' : sway(1.5 * out, w.pivot, 4) + (cycling ? beat(-out, w.pivot) : '')
  const feathers = w.feathers.map((fe, i) => `<path d="${fe.d}">${still ? '' : sway(2 * out, fe.pivot, 3, (i * 0.37) % 3)}</path>`).join('')
  return `<g>${move}<path d="${w.base}"/>${feathers}</g>`
}

function tail(still: boolean) {
  const t = ART.tail
  const flames = t.flames.map((fl, i) => `<path d="${fl.d}">${still ? '' : sway(i % 2 ? -3 : 3, fl.pivot, 1.8, i * 0.31)}</path>`).join('')
  return `<g>${still ? '' : sway(1, t.pivot, 5)}<path d="${t.base}"/>${flames}</g>`
}

// The heart sits under the strokes (it reaches a little beneath them, so no seam shows).
const emblem = (still: boolean, cycling: boolean) =>
  `<path d="${ART.heart}" style="fill:var(--inner)"/>${wing('left', still, cycling)}${wing('right', still, cycling)}${tail(still)}<path d="${ART.body}"/>`

// Embers: x, y, radius, sideways drift, seconds, delay. They're HTML over the <svg> (badges.ts's
// emberLayer), so the GPU moves them without redrawing the bird; each starts that far into its rise.
const EMBERS: readonly Ember[] = [[40, 88, 2.6, -8, 3.1, 0], [52, 96, 2, 6, 2.6, 0.8], [64, 78, 3, -3, 3.4, 1.6], [76, 96, 2.2, 8, 2.8, 0.4], [88, 88, 2.6, 5, 3.2, 2.1],
  [30, 40, 2, -10, 2.6, 1.2], [98, 40, 2, 10, 2.9, 2.6], [18, 22, 1.6, -6, 2.4, 0.6], [110, 22, 1.6, 6, 2.5, 1.8], [64, 56, 1.8, -4, 2.2, 3],
  [24, 58, 1.8, -7, 2.7, 1.9], [104, 58, 2, 7, 3, 0.2], [46, 66, 1.6, -5, 2.3, 2.4], [82, 66, 1.6, 5, 2.5, 1.1]]
const EMBERS_MINI: readonly Ember[] = [[40, 84, 12, -16, 2.3, 0], [76, 76, 13, 14, 2.7, 0.8], [58, 60, 11, -8, 2.1, 1.5], [92, 92, 12, 18, 2.5, 2.1]]
const embers = (list: readonly Ember[], size: number, rise: number, cls: string) =>
  emberLayer(list.map(([x, y, r, dx, dur, delay]) => [x, y, r, dx, dur, -delay] as const), size, { rise, end: 0.35, colour: (i) => (i % 3 ? '#ff8a2a' : '#ffe08a'), corner: false, cls })

// The still drawing (beside a name, in its ashes, reduced motion) never changes, so it's put in the
// page once and every still Phoenix <use>s it: the paths are 30 KB, and a leaderboard has dozens.
const STILL = 'phx-still'
function ensureStillDrawing() {
  if (typeof document === 'undefined' || document.getElementById('phoenix-defs')) return
  const el = document.createElement('div')
  el.id = 'phoenix-defs'
  el.style.cssText = 'position:absolute;width:0;height:0;overflow:hidden'
  el.innerHTML = `<svg width="0" height="0" aria-hidden="true"><defs><g id="${STILL}">${emblem(true, false)}</g></defs></svg>`
  document.body.prepend(el)
}

const reducedMotion = () => typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches

export type PhoenixOptions = {
  /** Width and height in px. */
  size?: number
  /** The small-size drawing (beside a name): fire and a few big embers, no motion. Defaults to on at 32px and below. */
  simple?: boolean
  /** The share of categories Ablaze, 0 to 1. */
  lit?: number
  /** Hidden from assistive tech (a label sits on the wrapper). */
  decorative?: boolean
}

let n = 0
export function phoenixSvg({ size = 128, simple = size <= 32, lit = 1, decorative = false }: PhoenixOptions = {}): string {
  const u = `phx${++n}`
  const still = simple || reducedMotion()
  // Moving, each draws its own (the motion is in the drawing); still, they share one.
  const E = still || lit <= 0 ? STILL : `${u}-emblem`
  if (E === STILL) ensureStillDrawing()
  const locked = lit <= 0
  const partial = !locked && lit < 1
  const cycling = !still && !partial
  const edge = f(122 - lit * 120) // the drawing runs y 2 to 122; fire below this, ash above
  const night = typeof document !== 'undefined' && document.documentElement.dataset.theme === 'night'
  const C = 'gradientUnits="userSpaceOnUse"'
  const radial = (id: string, cx: number, cy: number, r: number, stops: readonly (readonly [number, string, number?])[]) =>
    `<radialGradient id="${u}-${id}" cx="${cx}" cy="${cy}" r="${r}" ${C}>${stops.map(([o, c, a = 1]) => `<stop offset="${o}" stop-color="${c}" stop-opacity="${a}"/>`).join('')}</radialGradient>`
  const area = 'maskUnits="userSpaceOnUse" x="-30" y="-30" width="188" height="188"'
  const label = locked ? 'The Phoenix, in its ashes' : partial ? `The Phoenix, ${Math.round(lit * 8)} of 8 categories Ablaze` : 'The Phoenix'
  const a11y = decorative ? 'aria-hidden="true" focusable="false"' : `role="img" aria-label="${label}"`
  const open = (cls: string) => `<svg class="phx-badge${simple ? ' simple' : ''}${cls}" width="${size}" height="${size}" viewBox="0 0 128 128" ${a11y}>`
  const fit = (inner: string) => (simple ? `<g>${inner}</g>` : `<g transform="translate(64 62) scale(.92) translate(-64 -62)">${inner}</g>`)

  if (locked) {
    // In its ashes: grey, the embers in it breathing faintly.
    const L = night ? { base: '#3a352f', edge: '#1b1815', inner: '#26221e' } : { base: '#cfc5b3', edge: '#b3a78f', inner: '#e4dccd' }
    return `${open(' locked')}
      ${fit(`<use href="#${E}" transform="translate(0 2.4)" fill="${L.edge}" style="--inner:${L.edge}"/>
        <use href="#${E}" fill="${L.base}" style="--inner:${L.inner}"/>
        ${still ? '' : `<use href="#${E}" fill="#ff6a1a" style="--inner:transparent" opacity="0"><animate attributeName="opacity" values="0;.14;0" dur="3.4s" repeatCount="indefinite"/></use>`}`)}</svg>`
  }

  const defs = `<defs>
    ${simple && night
      ? radial('heat', 64, 62, 70, [[0, '#ffe07a'], [0.35, '#ffa230'], [0.65, '#f2561a'], [1, '#c42a16']])
      : radial('heat', 64, 62, 70, [[0, '#ffc24a'], [0.3, '#ff8a1e'], [0.55, '#f24a1a'], [0.8, '#c41a16'], [1, '#7e0a0a']])}
    ${radial('hot', 64, 50, 60, [[0, '#ffffff'], [0.35, '#fff4c4'], [0.7, '#ffd36a'], [1, '#ffb040']])}
    ${radial('core', 64, 50, 24, [[0, '#ffffff', 0.9], [0.45, '#fff3b8', 0.5], [1, '#ffd060', 0]])}
    ${radial('ash', 64, 58, 70, [[0, '#7a7068'], [0.45, '#4d443d'], [1, '#221c18']])}
    ${radial('ashdeep', 64, 58, 70, [[0, '#3a332e'], [1, '#14100e']])}
    ${radial('glow', 64, 60, 64, [[0, '#ffd27a', 0.8], [0.4, '#ff6a2a', 0.45], [0.72, '#e0281a', 0.16], [1, '#b0180c', 0]])}
    ${radial('flash', 64, 58, 64, [[0, '#ffffff', 1], [0.35, '#fff3c0', 0.7], [1, '#ffb040', 0]])}
    <linearGradient id="${u}-flow" x1="0" y1="0" x2="0" y2="40" ${C} spreadMethod="repeat">
      <stop offset="0" stop-color="#fff6c8" stop-opacity="0"/><stop offset=".45" stop-color="#fff6c8" stop-opacity=".32"/>
      <stop offset=".55" stop-color="#ffd25a" stop-opacity=".2"/><stop offset="1" stop-color="#fff6c8" stop-opacity="0"/>
      ${still ? '' : '<animateTransform attributeName="gradientTransform" type="translate" values="0 0;0 -40" dur="1.5s" repeatCount="indefinite"/>'}
    </linearGradient>
    <linearGradient id="${u}-line" x1="0" y1="-6" x2="0" y2="6" ${C}>
      <stop offset="0" stop-color="#ff5a1a" stop-opacity="0"/><stop offset=".45" stop-color="#ffe9a0"/><stop offset=".6" stop-color="#ffb030"/><stop offset="1" stop-color="#ff3a10" stop-opacity="0"/>
    </linearGradient>
    ${E === STILL ? '' : `<g id="${u}-emblem">${emblem(still, cycling)}</g>`}
    <mask id="${u}-shape" ${area}><use href="#${E}" fill="#fff" style="--inner:#fff"/></mask>
    <filter id="${u}-soft" x="-30%" y="-30%" width="160%" height="160%"><feGaussianBlur stdDeviation="3"/></filter>
    <mask id="${u}-ashm" ${area}><rect x="-30" y="-30" width="188" height="${partial ? f(edge + 30) : 0}" fill="#fff" filter="url(#${u}-soft)">
      ${cycling ? cycle('height', [0, 0, 160, 160, 0, 0], [0, T.ashIn, T.ashFull, T.ashHold, T.reborn, 1]) : ''}
    </rect></mask>
    <filter id="${u}-lick" x="-25%" y="-25%" width="150%" height="150%">
      <feTurbulence type="fractalNoise" baseFrequency="0.022 0.06" numOctaves="1" seed="7" result="n">
        <animate attributeName="baseFrequency" values="0.022 0.06;0.028 0.08;0.022 0.06" dur="2.6s" repeatCount="indefinite"/>
      </feTurbulence>
      <feDisplacementMap in="SourceGraphic" in2="n" scale="2.4" xChannelSelector="R" yChannelSelector="G"/>
      <feGaussianBlur stdDeviation=".2"/>
    </filter>
  </defs>`

  // The fire, and over it the ash its mask lets through, and the burn line at the ash's edge.
  const fire = `<use href="#${E}" fill="url(#${u}-heat)" style="--inner:url(#${u}-hot)"/>
    <rect x="-30" y="-30" width="188" height="188" fill="url(#${u}-flow)" mask="url(#${u}-shape)"/>
    <circle cx="64" cy="50" r="24" fill="url(#${u}-core)" mask="url(#${u}-shape)" opacity=".6">${still ? '' : '<animate attributeName="opacity" values=".4;.8;.4" dur="1.7s" repeatCount="indefinite"/>'}</circle>`
  const ash = !cycling && !partial ? '' : `<g mask="url(#${u}-ashm)"><use href="#${E}" fill="url(#${u}-ash)" style="--inner:url(#${u}-ashdeep)"/></g>
    <g mask="url(#${u}-shape)"><rect x="-30" y="${partial ? f(edge - 6) : -35}" width="188" height="12" fill="url(#${u}-line)" opacity="${partial ? 1 : 0}">
      ${partial ? (still ? '' : '<animate attributeName="opacity" values="1;.7;1" dur="1.4s" repeatCount="indefinite"/>') : `${cycle('y', [-35, -35, 125, 125, -35, -35], [0, T.ashIn, T.ashFull, T.ashHold, T.reborn, 1])}
      ${cycle('opacity', [0, 0, 1, 1, 0, 0, 1, 1, 0, 0], [0, T.ashIn, 0.67, 0.73, T.ashFull + 0.005, T.ashHold - 0.005, 0.83, 0.95, T.reborn + 0.005, 1])}`}
    </rect></g>`
  const body = fit(`<use href="#${E}" transform="translate(0 2.4)" fill="#2e0804" style="--inner:#2e0804"/>
    <g ${still ? '' : `filter="url(#${u}-lick)"`}>${fire}${ash}</g>`)

  if (simple) return `${open('')}${defs}${body}</svg>${reducedMotion() ? '' : embers(EMBERS_MINI, size, 170, 'phx mini')}`

  if (partial) {
    // Holding at its progress: a glow as strong as the fire, embers only off the lit part.
    const glow = `<circle cx="64" cy="${f(edge + 10)}" r="${f(30 + lit * 34)}" fill="url(#${u}-glow)" opacity="${f(0.35 + lit * 0.5)}"/>`
    return `${open(' partial')}${defs}${glow}${body}</svg>${still ? '' : embers(EMBERS.filter(([, y]) => y > edge), size, 64, 'phx')}`
  }

  const twinkle = fit(ART.sparkles.map((d, i) => `<path d="${d}" fill="#ffe2a0">${still ? '' : `<animate attributeName="opacity" values="1;.3;1" dur="${f(2.4 + (i % 3) * 0.5)}s" begin="-${f(i * 0.43)}s" repeatCount="indefinite"/>`}</path>`).join(''))
  if (still) return `${open('')}${defs}<circle cx="64" cy="60" r="60" fill="url(#${u}-glow)" opacity=".85"/>${twinkle}${body}</svg>`

  const glow = `<circle cx="64" cy="60" r="60" fill="url(#${u}-glow)">
    ${cycle('r', [60, 60, 74, 56, 40, 40, 62, 60], [0, T.flare, T.peak, 0.7, T.ashFull, T.ashHold, 0.95, 1])}
    ${cycle('opacity', [0.85, 0.85, 1, 0.6, 0.2, 0.2, 0.8, 0.85], [0, T.flare, T.peak, 0.7, T.ashFull, T.ashHold, 0.95, 1])}
  </circle>`
  const ring = `<circle cx="64" cy="58" r="30" fill="none" stroke="#ffd27a" stroke-width="6" opacity="0">
    <animate attributeName="r" values="30;30;78;78" keyTimes="0;${T.peak - 0.005};.76;1" dur="${DUR}s" repeatCount="indefinite"/>
    <animate attributeName="stroke-width" values="6;6;.5;.5" keyTimes="0;${T.peak - 0.005};.76;1" dur="${DUR}s" repeatCount="indefinite"/>
    <animate attributeName="opacity" values="0;0;.9;0;0" keyTimes="0;${T.peak - 0.006};${T.peak - 0.005};.76;1" dur="${DUR}s" repeatCount="indefinite"/>
  </circle>`
  const flash = `<circle cx="64" cy="58" r="64" fill="url(#${u}-flash)" opacity="0">
    <animate attributeName="opacity" values="0;0;.9;0;0" keyTimes="0;.632;${T.peak};.71;1" dur="${DUR}s" repeatCount="indefinite"/>
  </circle>`
  // Sparks fly out from the heart on the flare.
  const sparks = Array.from({ length: 14 }, (_, i) => {
    const a = i * (360 / 14) + (i % 2 ? 9 : 0)
    const to = 58 - (44 + (i % 3) * 8)
    return `<g transform="rotate(${f(a)} 64 58)"><ellipse cx="64" cy="46" rx="1.3" ry="3.4" fill="${i % 2 ? '#fff4c0' : '#ffb347'}" opacity="0">
      <animate attributeName="cy" values="46;46;${to};${to}" keyTimes="0;${T.peak};.77;1" dur="${DUR}s" repeatCount="indefinite" calcMode="spline" keySplines="0 0 1 1;.1 .7 .3 1;0 0 1 1"/>
      <animate attributeName="opacity" values="0;0;1;0;0" keyTimes="0;${T.peak};.66;.77;1" dur="${DUR}s" repeatCount="indefinite"/>
    </ellipse></g>`
  }).join('')
  // Embers stop while it's ash and come back with the fire (app.css: .phx.cycle, on the same 8 s).
  return `${open('')}${defs}${glow}${ring}${twinkle}${body}${flash}${sparks}</svg>${embers(EMBERS, size, 64, 'phx cycle')}`
}
